import { useEffect, useState } from 'react';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDemo, setActiveDemo] = useState('dashboard');
  const [messageLength, setMessageLength] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex flex-col items-start cursor-pointer" onClick={() => scrollToSection('home')}>
              <div className="text-[#1E4A8C] text-3xl font-bold tracking-tight">Leasa</div>
              <div className="w-12 h-0.5 bg-[#C9A77C] rounded-full"></div>
            </div>

            <div className="hidden md:flex items-center space-x-8">
              <button 
                onClick={() => scrollToSection('features')}
                className="text-[#3D3D3D] hover:text-[#1E4A8C] font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                Features
              </button>
              <button 
                onClick={() => scrollToSection('pricing')}
                className="text-[#3D3D3D] hover:text-[#1E4A8C] font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                Pricing
              </button>
              <button 
                onClick={() => scrollToSection('about')}
                className="text-[#3D3D3D] hover:text-[#1E4A8C] font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                About
              </button>
              <button 
                onClick={() => scrollToSection('demo')}
                className="text-[#3D3D3D] hover:text-[#1E4A8C] font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                Demo
              </button>
              <button 
                onClick={() => scrollToSection('contact')}
                className="px-6 py-2.5 bg-[#8B7355] text-white rounded-lg hover:bg-[#6F5B44] transition-all font-medium whitespace-nowrap cursor-pointer"
              >
                Contact Us
              </button>
            </div>

            <button className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer">
              <i className="ri-menu-line text-2xl text-[#3D3D3D]"></i>
            </button>
          </div>
        </div>
      </nav>

      <main>
        {/* Hero Section */}
        <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              alt="Hero Background"
              className="w-full h-full object-cover object-center"
              src="https://readdy.ai/api/search-image?query=bright%20minimalist%20modern%20apartment%20interior%20with%20cream%20beige%20walls%20natural%20light%20flowing%20through%20large%20windows%20simple%20elegant%20furniture%20neutral%20color%20palette%20warm%20welcoming%20atmosphere%20clean%20lines%20contemporary%20residential%20design%20peaceful%20living%20space%20soft%20shadows%20high%20quality&width=1920&height=1080&seq=leasa-hero-bright-minimal-interior-v2&orientation=landscape"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-white/60 via-white/40 to-white/60"></div>
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-32 text-center w-full">
            <div className="max-w-4xl mx-auto">
              <div className="mb-6 inline-block">
                <div className="flex flex-col items-center gap-4 mb-6">
                  <div className="relative">
                    <div className="flex flex-col items-center gap-2">
                      <div className="text-[#1E4A8C] text-6xl font-bold tracking-tight">Leasa</div>
                      <div className="w-16 h-1 bg-[#C9A77C] rounded-full"></div>
                    </div>
                  </div>
                </div>
              </div>

              <h1 className="text-4xl md:text-5xl font-bold text-[#3D3D3D] mb-6 leading-tight">
                Your 24/7 intelligent leasing agent
              </h1>

              <p className="text-lg md:text-xl text-[#6B6B6B] mb-4 max-w-3xl mx-auto leading-relaxed">
                Leasa answers every leasing call — day or night. She handles prospect questions, qualifies intent, and naturally guides callers toward booking a tour.
              </p>

              <p className="text-lg md:text-xl text-[#6B6B6B] mb-10 max-w-3xl mx-auto leading-relaxed font-medium">
                Qualified leads are passed to your leasing team, so they can focus on what matters most: leasing apartments.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
                <button 
                  onClick={() => scrollToSection('contact')}
                  className="w-full sm:w-auto px-8 py-4 bg-[#8B7355] text-white text-base font-semibold rounded-lg hover:bg-[#6F5B44] transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                >
                  Schedule a Demo
                </button>
                <button 
                  onClick={() => scrollToSection('features')}
                  className="w-full sm:w-auto px-8 py-4 bg-white text-[#8B7355] text-base font-semibold rounded-lg hover:bg-[#FAF8F5] transition-all border-2 border-[#8B7355] whitespace-nowrap cursor-pointer"
                >
                  Learn More
                </button>
              </div>

              <div className="grid md:grid-cols-3 gap-6 mt-16">
                <div className="bg-white/90 backdrop-blur-sm rounded-xl p-8 shadow-lg">
                  <div className="flex items-center justify-center w-16 h-16 bg-[#F5EFE7] rounded-full mb-4 mx-auto">
                    <i className="ri-24-hours-line text-3xl text-[#8B7355]"></i>
                  </div>
                  <h3 className="text-xl font-bold text-[#3D3D3D] mb-3">24/7 Availability</h3>
                  <p className="text-[#6B6B6B] text-sm leading-relaxed">
                    Answers every leasing call simultaneously — even during peak traffic. No missed opportunities. No voicemails. No holds.
                  </p>
                </div>

                <div className="bg-white/90 backdrop-blur-sm rounded-xl p-8 shadow-lg">
                  <div className="flex items-center justify-center w-16 h-16 bg-[#F5EFE7] rounded-full mb-4 mx-auto">
                    <i className="ri-calendar-check-line text-3xl text-[#8B7355]"></i>
                  </div>
                  <h3 className="text-xl font-bold text-[#3D3D3D] mb-3">Automated Tour Booking</h3>
                  <p className="text-[#6B6B6B] text-sm leading-relaxed">
                    Automate incoming calls to free up your team for high-value tasks
                  </p>
                </div>

                <div className="bg-white/90 backdrop-blur-sm rounded-xl p-8 shadow-lg">
                  <div className="flex items-center justify-center w-16 h-16 bg-[#F5EFE7] rounded-full mb-4 mx-auto">
                    <i className="ri-global-line text-3xl text-[#8B7355]"></i>
                  </div>
                  <h3 className="text-xl font-bold text-[#3D3D3D] mb-3">Bilingual Support</h3>
                  <p className="text-[#6B6B6B] text-sm leading-relaxed">
                    Fluent in English & español to serve a broader pool of prospects. Natural conversations in their preferred language.
                  </p>
                </div>
              </div>

              <div className="mt-16 text-center">
                <p className="text-2xl font-bold text-[#3D3D3D] mb-2">No missed calls. No lost prospects.</p>
                <p className="text-xl text-[#6B6B6B]">Just more tours — and more leases — without adding staff.</p>
              </div>
            </div>
          </div>

          <div 
            className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce cursor-pointer"
            onClick={() => scrollToSection('features')}
          >
            <i className="ri-arrow-down-line text-3xl text-[#8B7355]"></i>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-[#3D3D3D] mb-4">
                What Leasa <span className="text-[#8B7355]">can do for you</span>
              </h2>
              <p className="text-lg text-[#6B6B6B] max-w-3xl mx-auto">
                Leasa is a leasing AI agent who answers every call, offering immediate help with:
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  img: 'https://readdy.ai/api/search-image?query=modern%20leasing%20office%20bright%20natural%20light%20clean%20minimalist%20desk%20with%20computer%20professional%20property%20management%20workspace%20cream%20beige%20tones%20elegant%20simple%20furniture%20welcoming%20atmosphere%20contemporary%20interior%20design%20high%20quality&width=600&height=400&seq=leasa-feature-availability-v2&orientation=landscape',
                  icon: 'ri-phone-line',
                  title: 'Availability',
                  desc: 'Accurate, up-to-the-minute information on floor plans, pricing, and specials.'
                },
                {
                  img: 'https://readdy.ai/api/search-image?query=professional%20property%20manager%20reviewing%20calendar%20on%20tablet%20in%20bright%20modern%20office%20natural%20light%20cream%20walls%20elegant%20workspace%20scheduling%20appointments%20contemporary%20interior%20simple%20clean%20design%20high%20quality&width=600&height=400&seq=leasa-feature-scheduling-v2&orientation=landscape',
                  icon: 'ri-calendar-line',
                  title: 'Scheduling Tours',
                  desc: 'Instantly book and confirm qualified tours on your calendar.'
                },
                {
                  img: 'https://readdy.ai/api/search-image?query=friendly%20leasing%20conversation%20modern%20apartment%20lobby%20bright%20natural%20light%20professional%20interaction%20warm%20welcoming%20atmosphere%20cream%20beige%20interior%20contemporary%20residential%20design%20clean%20minimalist%20space%20high%20quality&width=600&height=400&seq=leasa-feature-faqs-v2&orientation=landscape',
                  icon: 'ri-question-answer-line',
                  title: 'Answering FAQs',
                  desc: 'Clear, consistent answers to lease terms, amenities, policies, and more.'
                },
                {
                  img: 'https://readdy.ai/api/search-image?query=professional%20reviewing%20applicant%20information%20on%20laptop%20modern%20bright%20office%20natural%20light%20clean%20workspace%20cream%20tones%20elegant%20simple%20desk%20contemporary%20business%20setting%20organized%20efficient%20high%20quality&width=600&height=400&seq=leasa-feature-qualifying-v2&orientation=landscape',
                  icon: 'ri-user-search-line',
                  title: 'Qualifying Leads',
                  desc: 'Can screen for income, credit, pets, move-in dates, and more.'
                },
                {
                  img: 'https://readdy.ai/api/search-image?query=beautiful%20modern%20apartment%20building%20exterior%20bright%20sunny%20day%20clean%20contemporary%20architecture%20professional%20multifamily%20residential%20property%20inviting%20entrance%20landscaped%20grounds%20wide%20angle%20high%20quality&width=600&height=400&seq=leasa-feature-property-v2&orientation=landscape',
                  icon: 'ri-building-line',
                  title: 'Property Information',
                  desc: 'Customized for your property with detailed amenity and community information.'
                },
                {
                  img: 'https://readdy.ai/api/search-image?query=modern%20analytics%20dashboard%20on%20computer%20screen%20bright%20office%20natural%20light%20clean%20workspace%20performance%20metrics%20charts%20graphs%20contemporary%20business%20setting%20cream%20tones%20professional%20environment%20high%20quality&width=600&height=400&seq=leasa-feature-analytics-v2&orientation=landscape',
                  icon: 'ri-line-chart-line',
                  title: 'Performance Analytics',
                  desc: '99% of daily lead calls covered — without extra pressure on your staff.'
                }
              ].map((feature, idx) => (
                <div key={idx} className="bg-[#FAF8F5] rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                  <div className="w-full h-48">
                    <img
                      alt={feature.title}
                      className="w-full h-full object-cover object-top"
                      src={feature.img}
                    />
                  </div>
                  <div className="p-8">
                    <div className="flex items-center justify-center w-14 h-14 bg-white rounded-full mb-4 mx-auto">
                      <i className={`${feature.icon} text-2xl text-[#8B7355]`}></i>
                    </div>
                    <h3 className="text-xl font-bold text-[#3D3D3D] mb-3 text-center">{feature.title}</h3>
                    <p className="text-[#6B6B6B] text-sm leading-relaxed text-center">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-16 text-center bg-[#F5EFE7] rounded-2xl p-8">
              <p className="text-xl font-semibold text-[#3D3D3D] mb-2">*Customized for your property</p>
              <p className="text-lg text-[#6B6B6B]">99% of daily lead calls covered — without extra pressure on your staff</p>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="py-24 bg-[#FAF8F5]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-[#3D3D3D] mb-4">
                Simple, <span className="text-[#8B7355]">transparent pricing</span>
              </h2>
              <p className="text-lg text-[#6B6B6B] max-w-3xl mx-auto">
                Choose the plan that fits your property management needs
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {/* Starter Plan */}
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-[#3D3D3D] mb-2">Starter</h3>
                <p className="text-[#6B6B6B] text-sm mb-6">Ready to go for single properties</p>
                <div className="mb-6">
                  <span className="text-5xl font-bold text-[#3D3D3D]">$699</span>
                  <span className="text-[#6B6B6B] text-lg">/month</span>
                </div>
                <ul className="space-y-4 mb-8">
                  {['24/7 Call Answering', 'Basic FAQ Responses', 'Tour Scheduling', 'Up to 200 calls/month', 'Email Support', 'Monthly Reports'].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-check-line text-[#8B7355] text-xl"></i>
                      </div>
                      <span className="text-[#6B6B6B] text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
                <button className="w-full py-3.5 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer bg-[#F5EFE7] text-[#8B7355] hover:bg-[#8B7355] hover:text-white">
                  Get Started
                </button>
              </div>

              {/* Professional Plan */}
              <div className="bg-white rounded-2xl p-8 ring-2 ring-[#8B7355] shadow-2xl transform md:scale-105">
                <div className="bg-[#8B7355] text-white text-xs font-bold px-4 py-1.5 rounded-full inline-block mb-4 whitespace-nowrap">
                  MOST POPULAR
                </div>
                <h3 className="text-2xl font-bold text-[#3D3D3D] mb-2">Professional</h3>
                <p className="text-[#6B6B6B] text-sm mb-6">Ideal for growing portfolios</p>
                <div className="mb-6">
                  <span className="text-5xl font-bold text-[#3D3D3D]">$899</span>
                  <span className="text-[#6B6B6B] text-lg">/month</span>
                </div>
                <ul className="space-y-4 mb-8">
                  {['Everything in Starter', 'Lead Scoring', 'Advanced Analytics', 'Up to 500 calls/month', 'Priority Support', 'Custom Integrations', 'Website Agent'].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-check-line text-[#8B7355] text-xl"></i>
                      </div>
                      <span className="text-[#6B6B6B] text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
                <button className="w-full py-3.5 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer bg-[#8B7355] text-white hover:bg-[#6F5B44] shadow-lg">
                  Get Started
                </button>
              </div>

              {/* Enterprise Plan */}
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-[#3D3D3D] mb-2">Enterprise</h3>
                <p className="text-[#6B6B6B] text-sm mb-6">For large property groups</p>
                <div className="mb-6">
                  <span className="text-5xl font-bold text-[#3D3D3D]">Custom</span>
                  <span className="text-[#6B6B6B] text-lg"></span>
                </div>
                <ul className="space-y-4 mb-8">
                  {['Everything in Professional', 'Unlimited Calls', 'Dedicated Account Manager', 'Custom AI Training', 'API Access', 'White-Label Options', 'SLA Guarantee'].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-check-line text-[#8B7355] text-xl"></i>
                      </div>
                      <span className="text-[#6B6B6B] text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
                <button className="w-full py-3.5 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer bg-[#F5EFE7] text-[#8B7355] hover:bg-[#8B7355] hover:text-white">
                  Get Started
                </button>
              </div>
            </div>

            <div className="mt-16 text-center">
              <p className="text-[#6B6B6B] text-sm">All plans include a 14-day free trial. No credit card required.</p>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center mb-24">
              <div>
                <h2 className="text-4xl md:text-5xl font-bold text-[#3D3D3D] mb-6">
                  Transforming <span className="text-[#8B7355]">multifamily operations</span>
                </h2>
                <p className="text-lg text-[#6B6B6B] mb-6 leading-relaxed">
                  Leasa was born from a simple observation: property management teams were missing valuable prospects due to high call volumes, after-hours inquiries, and limited staffing.
                </p>
                <p className="text-lg text-[#6B6B6B] mb-6 leading-relaxed">
                  We built an AI-powered leasing assistant that never sleeps, never takes a break, and handles every call with professionalism and care. Now, property managers can focus on what they do best—building relationships and closing leases.
                </p>
                <p className="text-lg text-[#6B6B6B] leading-relaxed">
                  Our mission is to help multifamily operations maximize their leasing potential while reducing operational stress.
                </p>
              </div>

              <div className="w-full h-[500px] rounded-2xl overflow-hidden shadow-xl">
                <img
                  alt="About Leasa"
                  className="w-full h-full object-cover object-top"
                  src="https://readdy.ai/api/search-image?query=bright%20minimalist%20modern%20apartment%20living%20room%20with%20cream%20beige%20furniture%20natural%20light%20through%20large%20windows%20simple%20elegant%20design%20neutral%20color%20palette%20peaceful%20atmosphere%20contemporary%20residential%20interior%20clean%20lines%20soft%20shadows%20welcoming%20space%20high%20quality&width=800&height=1000&seq=leasa-about-bright-living-v2&orientation=portrait"
                />
              </div>
            </div>

            <div className="mb-24">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                <div className="text-center">
                  <div className="text-5xl font-bold text-[#8B7355] mb-2">99%</div>
                  <div className="text-[#6B6B6B] font-medium">Call Coverage Rate</div>
                </div>
                <div className="text-center">
                  <div className="text-5xl font-bold text-[#8B7355] mb-2">24/7</div>
                  <div className="text-[#6B6B6B] font-medium">Always Available</div>
                </div>
                <div className="text-center">
                  <div className="text-5xl font-bold text-[#8B7355] mb-2">60%+</div>
                  <div className="text-[#6B6B6B] font-medium">Time Saved on Inquiries</div>
                </div>
                <div className="text-center">
                  <div className="text-5xl font-bold text-[#8B7355] mb-2">50%+</div>
                  <div className="text-[#6B6B6B] font-medium">Tour Booking Increase</div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-3xl font-bold text-[#3D3D3D] mb-12 text-center">Our Core Values</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {[
                  { icon: 'ri-lightbulb-line', title: 'Innovation', desc: 'Leveraging cutting-edge AI to transform property management' },
                  { icon: 'ri-shield-check-line', title: 'Reliability', desc: 'Consistent, professional service your prospects can count on' },
                  { icon: 'ri-team-line', title: 'Partnership', desc: 'Working alongside your team to achieve better results' },
                  { icon: 'ri-heart-line', title: 'Excellence', desc: 'Committed to delivering exceptional experiences every time' }
                ].map((value) => (
                  <div key={value.title} className="bg-[#FAF8F5] rounded-xl p-8 text-center">
                    <div className="flex items-center justify-center w-16 h-16 bg-white rounded-full mb-4 mx-auto">
                      <i className={`${value.icon} text-3xl text-[#8B7355]`}></i>
                    </div>
                    <h4 className="text-xl font-bold text-[#3D3D3D] mb-3">{value.title}</h4>
                    <p className="text-[#6B6B6B] text-sm leading-relaxed">{value.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Demo Section */}
        <section id="demo" className="py-24 bg-[#FAF8F5]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-[#3D3D3D] mb-4">
                See Leasa.pro <span className="text-[#8B7355]">in action</span>
              </h2>
              <p className="text-lg text-[#6B6B6B] max-w-3xl mx-auto">
                Explore how our platform transforms property management workflows
              </p>
            </div>

            <div className="mb-8 flex flex-wrap justify-center gap-4">
              {['Dashboard Overview', 'AI Assistant', 'Resident Portal'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveDemo(tab.toLowerCase().replace(' ', '-'))}
                  className={`px-6 py-3 rounded-xl font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeDemo === tab.toLowerCase().replace(' ', '-')
                      ? 'bg-[#8B7355] text-white shadow-lg'
                      : 'bg-white text-[#6B6B6B] hover:bg-[#F5EFE7]'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="p-8 md:p-12">
                <h3 className="text-2xl font-bold text-[#3D3D3D] mb-3">Dashboard Overview</h3>
                <p className="text-base text-[#6B6B6B] mb-8">
                  Get a complete view of your property operations at a glance
                </p>
                <div className="w-full h-[500px] rounded-xl overflow-hidden bg-[#FAF8F5]">
                  <img
                    alt="Dashboard Overview"
                    className="w-full h-full object-cover object-top"
                    src="https://readdy.ai/api/search-image?query=modern%20clean%20property%20management%20dashboard%20interface%20with%20warm%20earth%20tones%20natural%20wood%20elements%20comfortable%20workspace%20analytics%20charts%20elegant%20minimalist%20design%20Apple%20style%20interface%20high%20quality&width=1200&height=700&seq=leasa-demo-dashboard-earth-01&orientation=landscape"
                  />
                </div>
              </div>
            </div>

            <div className="mt-12 text-center">
              <button 
                onClick={() => scrollToSection('contact')}
                className="px-8 py-4 bg-[#8B7355] text-white text-base font-semibold rounded-xl hover:bg-[#6F5B44] transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                Schedule a Live Demo
              </button>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-[#3D3D3D] mb-4">
                Get in <span className="text-[#8B7355]">touch</span>
              </h2>
              <p className="text-lg text-[#6B6B6B] max-w-3xl mx-auto">
                Ready to transform your property management? Let's talk about how Leasa can help.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-16 items-start">
              {/* Contact Info */}
              <div>
                <div className="bg-[#FAF8F5] rounded-2xl p-8 mb-8">
                  <h3 className="text-2xl font-bold text-[#3D3D3D] mb-6">Contact Information</h3>
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full">
                        <i className="ri-mail-line text-xl text-[#8B7355]"></i>
                      </div>
                      <div>
                        <div className="font-semibold text-[#3D3D3D] mb-1">Email</div>
                        <a
                          href="mailto:hello@leasa.pro"
                          className="text-[#6B6B6B] hover:text-[#8B7355] transition-colors cursor-pointer"
                        >
                          hello@leasa.pro
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full">
                        <i className="ri-phone-line text-xl text-[#8B7355]"></i>
                      </div>
                      <div>
                        <div className="font-semibold text-[#3D3D3D] mb-1">Phone</div>
                        <a
                          href="tel:+18007876228"
                          className="text-[#6B6B6B] hover:text-[#8B7355] transition-colors cursor-pointer"
                        >
                          (800) 787-6228
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full">
                        <i className="ri-time-line text-xl text-[#8B7355]"></i>
                      </div>
                      <div>
                        <div className="font-semibold text-[#3D3D3D] mb-1">Business Hours</div>
                        <div className="text-[#6B6B6B]">Monday - Friday: 9am - 6pm EST</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-[#FAF8F5] rounded-2xl p-8">
                  <h3 className="text-xl font-bold text-[#3D3D3D] mb-4">Why Choose Leasa?</h3>
                  <ul className="space-y-3">
                    {[
                      'Reduce missed calls by 99%',
                      'Increase tour bookings automatically',
                      'Free up staff for high-value tasks',
                      '24/7 professional service'
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-3">
                        <i className="ri-check-line text-[#8B7355] text-xl mt-0.5"></i>
                        <span className="text-[#6B6B6B] text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-white border-2 border-[#F5EFE7] rounded-2xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-[#3D3D3D] mb-6">Send us a message</h3>
                <form className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-[#3D3D3D] mb-2">
                      Name *
                    </label>
                    <input
                      id="name"
                      required
                      className="w-full px-4 py-3 bg-[#FAF8F5] border border-[#F5EFE7] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B7355] text-[#3D3D3D] text-sm"
                      placeholder="Your name"
                      name="name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-[#3D3D3D] mb-2">
                      Email *
                    </label>
                    <input
                      id="email"
                      required
                      className="w-full px-4 py-3 bg-[#FAF8F5] border border-[#F5EFE7] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B7355] text-[#3D3D3D] text-sm"
                      placeholder="your.email@company.com"
                      type="email"
                      name="email"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-[#3D3D3D] mb-2">
                      Phone
                    </label>
                    <input
                      id="phone"
                      className="w-full px-4 py-3 bg-[#FAF8F5] border border-[#F5EFE7] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B7355] text-[#3D3D3D] text-sm"
                      placeholder="(555) 123-4567"
                      type="tel"
                      name="phone"
                    />
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-semibold text-[#3D3D3D] mb-2">
                      Company
                    </label>
                    <input
                      id="company"
                      className="w-full px-4 py-3 bg-[#FAF8F5] border border-[#F5EFE7] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B7355] text-[#3D3D3D] text-sm"
                      placeholder="Your company name"
                      name="company"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-[#3D3D3D] mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={4}
                      maxLength={500}
                      onChange={(e) => setMessageLength(e.target.value.length)}
                      className="w-full px-4 py-3 bg-[#FAF8F5] border border-[#F5EFE7] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B7355] text-[#3D3D3D] text-sm resize-none"
                      placeholder="Tell us about your property management needs..."
                    />
                    <div className="text-xs text-[#6B6B6B] mt-1 text-right">{messageLength}/500 characters</div>
                  </div>

                  <button
                    type="submit"
                    className="w-full px-8 py-4 bg-[#8B7355] text-white font-semibold rounded-lg hover:bg-[#6F5B44] transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    Send Message
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-[#3D3D3D] text-white py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex flex-col items-start mb-4">
                <div className="text-[#1E4A8C] text-3xl font-bold tracking-tight">Leasa</div>
                <div className="w-12 h-0.5 bg-[#C9A77C] rounded-full mt-1"></div>
              </div>
              <p className="text-[#D1D1D1] text-sm leading-relaxed">
                Your 24/7 intelligent leasing agent for multifamily operations.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4 text-white">Product</h4>
              <ul className="space-y-3">
                <li>
                  <button 
                    onClick={() => scrollToSection('features')}
                    className="text-[#D1D1D1] hover:text-[#C9A77C] transition-colors text-sm whitespace-nowrap cursor-pointer"
                  >
                    Features
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection('pricing')}
                    className="text-[#D1D1D1] hover:text-[#C9A77C] transition-colors text-sm whitespace-nowrap cursor-pointer"
                  >
                    Pricing
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection('demo')}
                    className="text-[#D1D1D1] hover:text-[#C9A77C] transition-colors text-sm whitespace-nowrap cursor-pointer"
                  >
                    Demo
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4 text-white">Company</h4>
              <ul className="space-y-3">
                <li>
                  <button 
                    onClick={() => scrollToSection('about')}
                    className="text-[#D1D1D1] hover:text-[#C9A77C] transition-colors text-sm whitespace-nowrap cursor-pointer"
                  >
                    About Us
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection('contact')}
                    className="text-[#D1D1D1] hover:text-[#C9A77C] transition-colors text-sm whitespace-nowrap cursor-pointer"
                  >
                    Contact
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4 text-white">Connect</h4>
              <div className="flex gap-4">
                <a href="#" className="w-10 h-10 flex items-center justify-center bg-[#5A5A5A] hover:bg-[#C9A77C] rounded-full transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-xl"></i>
                </a>
                <a href="#" className="w-10 h-10 flex items-center justify-center bg-[#5A5A5A] hover:bg-[#C9A77C] rounded-full transition-colors cursor-pointer">
                  <i className="ri-twitter-x-fill text-xl"></i>
                </a>
                <a href="#" className="w-10 h-10 flex items-center justify-center bg-[#5A5A5A] hover:bg-[#C9A77C] rounded-full transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-[#5A5A5A] pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-[#D1D1D1] text-sm">© 2026 Leasa.pro. All rights reserved.</p>
              <div className="flex gap-6">
                <a href="#" className="text-[#D1D1D1] hover:text-[#C9A77C] text-sm transition-colors whitespace-nowrap cursor-pointer">
                  Privacy Policy
                </a>
                <a href="#" className="text-[#D1D1D1] hover:text-[#C9A77C] text-sm transition-colors whitespace-nowrap cursor-pointer">
                  Terms of Service
                </a>
                <a
                  href="https://readdy.ai/?ref=logo"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#D1D1D1] hover:text-[#C9A77C] text-sm transition-colors whitespace-nowrap cursor-pointer"
                >
                  Powered by Readdy
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
